/**
 * Permissions store.
 *
 * ── Tenant identity model ──
 *
 * The backend represents a tenant in two forms, each authoritative for a
 * different domain. Both are required; code must not rewrite one form into the
 * other outside the conversions described below.
 *
 *   • id (dash)                              e.g. "gls-de"
 *       Resource identity. Used in the `tenants[]` list, REST resource paths
 *       (`/api/tenant-configuration/gls-de`), route params / URLs, and analytics.
 *
 *   • scope path (underscore, dot-separated) e.g. "gls_de.DE.2.34"
 *       The permission hierarchy: `tenant.country.region|group.depot.suco`.
 *       Used as the keys of the `actions` map (see `depotPaths`, `tenantScopes`).
 *
 * The forms differ only in the tenant segment ("gls-de" ↔ "gls_de"); the dot
 * delimits hierarchy levels. Resources are keyed by the dash `id`; permissions
 * are keyed by the underscore scope `path`.
 *
 * ── Normalization rule ──
 *
 * Convert toward the form used by the comparison target:
 *   • target is an action/scope path (underscore keys) → `-`→`_`
 *       (`formatPath`, `getActionsForPath`, `tenantContextTenants`)
 *   • target is `tenants[].id` (dash)                  → `_`→`-`
 *       (`hasTenantAccess`, `selectedDepotTenant`)
 *
 * Since the swap only affects the tenant segment, converting a value already in
 * the target form is a no-op; the affected checks therefore accept either form.
 * Route params arrive in `id` (dash) form, and `hasTenantAccess` normalizes so
 * the underscore scope form resolves as well.
 */

import { computed, ref } from 'vue';

import { defineStore } from 'pinia';

import type { TenantConfigurationResponse } from '@/composables/api/tenants/types';
import type { Role } from '@/composables/api/types';
import constants from '@/config/constants';
import depotFeaturesDef from '@/services/permissions/features';
import subcontractorFeaturesDef from '@/services/permissions/subcontractorFeatures';
import tenantFeaturesDef from '@/services/permissions/tenantFeatures';
import type { Tenant } from '@/types/api/tenants';
import type { Context, ContextName } from '@/types/permissions/context';

// ── Types ──────────────────────────────────────────────────────────

type ContextKey = 'ADMIN' | 'TENANT' | 'DEPOT' | 'SUBCONTRACTOR';

/** Raw actions shape from backend: Record<ContextKey, Record<Path, ActionName[]>> */
type ActionsMap = Partial<Record<ContextKey, Record<string, string[]>>>;

// Derive feature name literals from the `as const` arrays so indexing returns `boolean`, not `boolean | undefined`
type DepotFeatureName = (typeof depotFeaturesDef)[number]['name'];
type TenantFeatureName = (typeof tenantFeaturesDef)[number]['name'];
type SubcontractorFeatureName =
  (typeof subcontractorFeaturesDef)[number]['name'];
export type FeatureFlags = Record<
  DepotFeatureName | TenantFeatureName | SubcontractorFeatureName,
  boolean
>;

// ── Store ──────────────────────────────────────────────────────────

export const usePermissionsStore = defineStore('permissions', () => {
  const userContexts = constants.rolesContexts;

  // ── Raw data from backend ──
  const actions = ref<ActionsMap>({});
  const tenants = ref<Tenant[]>([]);

  // ── Mutable selection state (private) ──
  const _selectedContext = ref<string>('');
  const _selectedTenant = ref<string>('');
  const _selectedDepotPath = ref<string>('');
  const _tenantConfiguration = ref<TenantConfigurationResponse | null>(null);

  // ── Hydration ──
  const hydrate = (userActions: ActionsMap, userTenants: Tenant[]) => {
    actions.value = userActions;
    tenants.value = userTenants;
  };

  const reset = () => {
    actions.value = {};
    tenants.value = [];
    _selectedContext.value = '';
    _selectedTenant.value = '';
    _selectedDepotPath.value = '';
    _tenantConfiguration.value = null;
  };

  // ── Helper functions ──
  /**
   * id-form → scope-path form (`-`→`_`), e.g. "gls-de.DE" → "gls_de.DE".
   * Applied when a URL/id value must be matched against the underscore-keyed
   * `actions`/paths. See the tenant identity model at the top of this file.
   */
  const formatPath = (path: string | null): string | null => {
    return path ? path.replaceAll('-', '_') : null;
  };

  // ── Selection getters/setters ──
  const selectedContext = computed({
    get: () => _selectedContext.value,
    set: (val: string) => {
      _selectedContext.value = val;
    }
  });

  const selectedTenant = computed({
    get: () => _selectedTenant.value,
    set: (val: string) => {
      _selectedTenant.value = val;
    }
  });

  const selectedDepotPath = computed({
    get: () => _selectedDepotPath.value,
    set: (val: string) => {
      _selectedDepotPath.value = val;
    }
  });

  const tenantConfiguration = computed({
    get: () => _tenantConfiguration.value,
    set: (val: TenantConfigurationResponse | null) => {
      _tenantConfiguration.value = val;
    }
  });

  // ── Core permission checks ──
  const isAdmin = computed(() => {
    return !!actions.value?.ADMIN?.admin?.includes('admin');
  });

  const isSuperAdmin = computed(() => {
    return (
      isAdmin.value && actions.value?.ADMIN?.admin?.includes('super-admin')
    );
  });

  const tenantPermissions = computed(() => {
    return actions.value?.TENANT;
  });

  const isSubContractor = computed(() => {
    return !!actions.value?.SUBCONTRACTOR;
  });

  const depotOrSuCo = computed(() => {
    return isSubContractor.value
      ? userContexts.SUBCONTRACTOR
      : userContexts.DEPOT;
  });

  const depotActions = computed(() => {
    return actions.value[depotOrSuCo.value];
  });

  const depotPaths = computed(() => {
    if (!depotActions.value) return null;
    return Object.keys(depotActions.value);
  });

  const depotPath = computed(() => {
    return formatPath(_selectedDepotPath.value);
  });

  const tenantPath = computed(() => {
    return formatPath(_selectedTenant.value);
  });

  const subcontractorId = computed(() => {
    if (!isSubContractor.value || !depotPath.value) return null;
    const matchingElement = depotPaths.value?.find((element) =>
      element.startsWith(depotPath.value as string)
    );
    if (matchingElement) {
      const parts = matchingElement.split('.');
      return parts[parts.length - 1];
    }
    return null;
  });

  const currentSelectedPath = computed(() => {
    switch (_selectedContext.value) {
      case constants.scopeTypes.ADMIN:
        return '';
      case constants.scopeTypes.SUBCONTRACTOR:
        return subcontractorId.value
          ? `${depotPath.value}.${subcontractorId.value}`
          : null;
      case constants.scopeTypes.DISTRIBUTION_CENTER:
        return isSubContractor.value
          ? `${depotPath.value}.${subcontractorId.value}`
          : depotPath.value;
      case constants.scopeTypes.TENANT:
        return tenantPath.value;
      default:
        return null;
    }
  });

  const selectedDepotTenant = computed(() => {
    if (_selectedContext.value !== constants.scopeTypes.DISTRIBUTION_CENTER) {
      return false;
    }
    const path = currentSelectedPath.value?.split('.')[0];
    return path ? path.replaceAll('_', '-') : false;
  });

  // ── Tenant scopes ──
  const tenantScopes = computed(() => {
    return Object.keys(tenantPermissions.value ?? {});
  });

  const isTenantAdmin = computed(() => {
    return tenantScopes.value[0] === 'tenant-admin';
  });

  const hasSingleTenantScope = computed(() => {
    if (isAdmin.value) return false;
    return (
      tenantScopes.value.length === 1 &&
      tenantScopes.value[0] !== 'tenant-admin'
    );
  });

  const singleTenantScope = computed(() => {
    return tenantScopes.value[0];
  });

  // ── Tenant checks ──
  const isTenantAdminForAnyPath = (): boolean => {
    if (!tenantPermissions.value) return false;
    return Object.values(tenantPermissions.value).some((permission) =>
      permission.includes('tenant-admin')
    );
  };

  const isTenantAdminForPath = (path: string | null = null): boolean => {
    if (isAdmin.value || isTenantAdmin.value) return true;
    let pathToCheck = path ? path : currentSelectedPath.value;
    if (!pathToCheck) return false;
    const [tenantScope] = pathToCheck?.split('.') ?? [];
    if (!tenantScope || !tenantPermissions.value) return false;
    return Boolean(
      tenantPermissions.value[tenantScope]?.includes('tenant-admin')
    );
  };

  const hasTenantInTenantActions = (path: string): boolean => {
    if (!path) return false;
    const [tenantScope] = path.split('.');
    if (!tenantScope) return false;
    return !!tenantPermissions.value?.[tenantScope];
  };

  // ── Context management ──
  const getContextsFromConstants = (contexts: string[]): Context[] => {
    return constants.contexts.filter((c) => contexts.includes(c.code));
  };

  const getContextsFromActions = (): Context[] => {
    const contextKeys = Object.keys(actions.value || []);
    const showContexts: Context[] = [];

    if (contextKeys.includes(userContexts.ADMIN)) {
      return getContextsFromConstants([
        userContexts.ADMIN,
        userContexts.TENANT,
        userContexts.DEPOT
      ]);
    }

    if (isTenantAdminForAnyPath())
      return getContextsFromConstants([
        userContexts.TENANT,
        userContexts.DEPOT
      ]);

    if (contextKeys.includes(userContexts.TENANT))
      showContexts.push(...getContextsFromConstants([userContexts.TENANT]));
    if (contextKeys.includes(userContexts.DEPOT))
      showContexts.push(...getContextsFromConstants([userContexts.DEPOT]));
    // Subcontractor users get their own first-class context (previously funneled
    // to Depot). Their suco identity is derived from the SUBCONTRACTOR action
    // bucket; data + nav scope to the subcontractor.
    if (contextKeys.includes(userContexts.SUBCONTRACTOR))
      return getContextsFromConstants([userContexts.SUBCONTRACTOR]);

    return showContexts;
  };

  const contexts = computed(() => {
    return getContextsFromActions();
  });

  const hasSingleContext = computed(() => {
    return contexts.value?.length === 1;
  });

  const hasContext = (contextToCheck: ContextName): boolean => {
    return (
      contexts.value.filter((context) => context.name === contextToCheck)
        .length === 1
    );
  };

  /**
   * Tenant access check used by the router's tenant guard.
   * Normalizes the incoming value scope-path → id form (`_`→`-`) before matching
   * the dash-keyed `tenants[].id` list, so both "gls-de" and "gls_de" resolve to
   * the tenant "gls-de". See the tenant identity model at the top of this file.
   */
  const hasTenantAccess = (tenantId: string): boolean => {
    return (
      tenants.value.filter(
        (tenant) => tenant.id === tenantId.replaceAll('_', '-')
      ).length === 1
    );
  };

  // ── Actions for paths ──
  /**
   * Resolves the actions granted at a given scope path.
   * Converts the incoming path id-form → scope-path form (`-`→`_`) before
   * matching the underscore-keyed `depotPaths`, then walks the hierarchy
   * (tenant.country.region.depot.suco). See the tenant identity model at the
   * top of this file.
   */
  const getActionsForPath = (pathToCheck: string | null): string[] | null => {
    if (!pathToCheck || !depotPaths.value || !depotActions.value) return null;

    const [pathTenant, pathCountry, pathRegion, pathDepot, pathSuco] =
      pathToCheck.split('.').map((s) => s.replaceAll('-', '_'));

    const paths = depotPaths.value.filter((p) => {
      const scopes = p.split('.');
      const [tenant, country, region, depot, suco] = scopes;
      return (
        pathTenant === tenant &&
        (pathCountry === country || !country) &&
        (pathRegion === region || !region) &&
        (pathDepot === depot || !depot) &&
        (pathSuco === suco || !suco || !pathSuco)
      );
    });

    const actionsList = paths
      .map((path) => depotActions.value?.[path])
      .flat()
      .filter((action): action is string => Boolean(action));

    return actionsList.length ? actionsList : null;
  };

  const hasActionForPath = (
    actionName: string,
    path: string | null = null
  ): boolean => {
    if (isTenantAdminForPath(path)) return true;
    const pathToCheck = path ?? depotPath.value;
    const actionsList = getActionsForPath(pathToCheck);
    if (!actionsList) return false;
    return actionsList.includes(actionName);
  };

  const getTenantActionsForPath = (pathToCheck: string): string[] | null => {
    return tenantPermissions.value?.[pathToCheck] || null;
  };

  const hasActionForTenantPath = (actionName: string): boolean => {
    if (isTenantAdmin.value || isAdmin.value) return true;
    const tenantPathValue = tenantPath.value || singleTenantScope.value;
    if (!tenantPathValue) return false;
    if (isTenantAdminForPath(tenantPathValue)) return true;

    const actionsList = getTenantActionsForPath(tenantPathValue);

    if (!actionsList) return false;
    return actionsList.includes(actionName);
  };

  // ── Features ──

  // Default map with every feature set to false (ensures all keys always exist)
  const featureDefaults = [
    ...depotFeaturesDef,
    ...tenantFeaturesDef,
    ...subcontractorFeaturesDef
  ].reduce((acc, { name }) => {
    acc[name as keyof FeatureFlags] = false;
    return acc;
  }, {} as FeatureFlags);

  const depotProperFeatures = computed(() => {
    return depotFeaturesDef.reduce((feature, { name, action }) => {
      feature[name as keyof FeatureFlags] = hasActionForPath(action);
      return feature;
    }, {} as FeatureFlags);
  });

  // Subcontractor context features resolve the suco feature catalogue against
  // the user's SUBCONTRACTOR action bucket (depotActions points at SUBCONTRACTOR
  // when isSubContractor). This is what lets the Subcontractor context diverge
  // from Depot without sharing features.ts.
  const subcontractorProperFeatures = computed(() => {
    return subcontractorFeaturesDef.reduce((feature, { name, action }) => {
      feature[name as keyof FeatureFlags] = hasActionForPath(action);
      return feature;
    }, {} as FeatureFlags);
  });

  const tenantFeatures = computed(() => {
    return tenantFeaturesDef.reduce((feature, { name, action }) => {
      feature[name as keyof FeatureFlags] = hasActionForTenantPath(action);
      return feature;
    }, {} as FeatureFlags);
  });

  const depotFeatures = computed((): FeatureFlags => {
    return { ...featureDefaults, ...depotProperFeatures.value };
  });

  const subcontractorFeatures = computed((): FeatureFlags => {
    return { ...featureDefaults, ...subcontractorProperFeatures.value };
  });

  const features = computed((): FeatureFlags => {
    // Return the right features based on the context
    if (_selectedContext.value === constants.scopeTypes.SUBCONTRACTOR)
      return subcontractorFeatures.value;
    if (_selectedContext.value === constants.scopeTypes.DISTRIBUTION_CENTER)
      return depotFeatures.value;
    if (_selectedContext.value === constants.scopeTypes.TENANT)
      return { ...featureDefaults, ...tenantFeatures.value };
    if (_selectedContext.value === constants.scopeTypes.ADMIN)
      return {
        ...featureDefaults,
        ...depotProperFeatures.value,
        ...tenantFeatures.value
      };
    return { ...featureDefaults };
  });

  // ── Tenants ──
  const availableTenants = computed(() => {
    // Copy array before sorting to avoid mutating the original
    return [...tenants.value].sort((a, b) => (a.name >= b.name ? 1 : -1));
  });

  const tenantContextTenants = computed(() => {
    if (!tenants.value?.length) return [];
    if (isAdmin.value || isTenantAdmin.value) return availableTenants.value;
    if (Object.keys(actions.value).includes(userContexts.TENANT)) {
      const tenantActions = actions.value[userContexts.TENANT];
      if (!tenantActions) return [];
      const tenantActionKeys = Object.keys(tenantActions);
      return availableTenants.value.filter((tenant) =>
        tenantActionKeys.includes(tenant.id.replaceAll('-', '_'))
      );
    }
    return [];
  });

  const isSingleTenantScope = computed(() => {
    return hasSingleTenantScope.value;
  });

  // ── Roles ──
  const getAvailableRolesByContext = (
    roles: Role[] | null,
    isForFilter: boolean
  ): Role[] | null => {
    if (!roles) return null;

    // If no roles are sent to the request backend will return all roles
    if (_selectedContext.value === constants.scopeTypes.ADMIN) {
      if (isForFilter) {
        return roles;
      }
      return [];
    }

    if (_selectedContext.value === constants.scopeTypes.DISTRIBUTION_CENTER) {
      const selectedTenantValue = depotPath.value?.split('.')[0];
      return roles.filter(
        ({ visibilityPath }) => visibilityPath === selectedTenantValue
      );
    }

    if (_selectedContext.value === constants.scopeTypes.TENANT) {
      return roles.filter(
        ({ visibilityPath, context }) =>
          visibilityPath === tenantPath.value ||
          (visibilityPath === '' && context === constants.rolesContexts.TENANT)
      );
    }

    return null;
  };

  return {
    // Raw data
    actions,
    tenants,

    // Selection state (computed with getters/setters)
    selectedContext,
    selectedTenant,
    selectedDepotPath,
    tenantConfiguration,

    // Derived state (all computed)
    isAdmin,
    isSuperAdmin,
    isSubContractor,
    isTenantAdmin,
    hasSingleContext,
    hasSingleTenantScope,
    isSingleTenantScope,
    contexts,
    features,
    depotFeatures,
    tenantFeatures,
    subcontractorFeatures,
    depotPath,
    tenantPath,
    currentSelectedPath,
    selectedDepotTenant,
    availableTenants,
    tenantContextTenants,
    tenantPermissions,
    tenantScopes,
    singleTenantScope,
    subcontractorId,
    depotOrSuCo,
    depotPaths,
    depotActions,

    // Methods
    hydrate,
    reset,
    hasContext,
    hasTenantAccess,
    hasActionForPath,
    hasActionForTenantPath,
    isTenantAdminForPath,
    isTenantAdminForAnyPath,
    getActionsForPath,
    getAvailableRolesByContext,
    hasTenantInTenantActions,
    getTenantActionsForPath
  };
});
